/**
 * @file previousItemRemovals.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description Taking an item off the Item picker's "Recently ordered" /
 *              "Recently bought" list, for everyone, until it is ordered again.
 *
 *              Cam, 30 Oct 2026: "Can we add a X button aligned right to each
 *              item in create order/ invoice/ PO etc. that shows previously
 *              ordered items, so users can remove items from that list, and
 *              items are removed for everyone, and only join the list again
 *              if/when the removed item is ordered again following its
 *              removal?"
 *
 *              Two lists, one rule:
 *                - 'customer' — a customer's "Recently ordered" (Create / Edit
 *                  Customer Order, Create Invoice incl. Add credit / debit);
 *                - 'supplier' — a supplier's "Recently bought" (Create / Edit
 *                  PO, Add / Edit Bill and Supplier Credit).
 *              A removal is per account + item and shared by everyone. The
 *              item stays off while its latest use in that list's own history
 *              (the moment it was ordered / bought — utils/previousItemsSql)
 *              is no later than the removal; ordered again after it, it is
 *              back by itself (`hiddenByRemoval` is the SQL's rule in JS).
 *
 *              Who may: anyone who can create or edit that side's documents
 *              (REMOVAL_PERMISSIONS). A view-only badge (REP) never — its
 *              writes are refused app-wide (middleware/readOnly.js).
 *
 *              Written once, here, as an ES module; the server's
 *              utils/previousItemRemovals.js is generated from it by
 *              scripts/sync-shared-utils.js — edit this file, then run
 *              `node scripts/sync-shared-utils.js`.
 */

/** The two lists. */
export const PREVIOUS_ITEM_SIDES = ['customer', 'supplier'];

/** Each list's name, as its heading says it (lower case, for sentences). */
export const REMOVAL_LIST_NAMES = {
  customer: 'recently ordered',
  supplier: 'recently bought',
};

/** Who may take an item off each list: any one of these. */
export const REMOVAL_PERMISSIONS = {
  customer: ['orders.create', 'orders.edit', 'invoices.create', 'invoices.edit'],
  supplier: ['purchase_orders.create', 'purchase_orders.edit', 'bills.create', 'bills.edit'],
};

const PERMISSION_LABELS = {
  'orders.create': 'Create sales orders',
  'orders.edit': 'Edit sales orders',
  'invoices.create': 'Create invoices',
  'invoices.edit': 'Edit invoices',
  'purchase_orders.create': 'Create purchase orders',
  'purchase_orders.edit': 'Edit purchase orders',
  'bills.create': 'Create bills',
  'bills.edit': 'Edit bills',
};

/** The X's title. */
export const REMOVE_TITLE = 'Remove from this list for everyone — it comes back when it is ordered again';

/** Before migrations/previous_item_removals.sql has run. */
export const NOT_READY_REASON = 'Not ready — migration previous_item_removals.sql has not run';

/** A view-only (REP) account. */
export const READ_ONLY_REASON = 'View only — your account can’t change this list';

/** The side, or null when it is not one of the two. */
export function normaliseSide(side) {
  const s = String(side ?? '').trim().toLowerCase();
  return PREVIOUS_ITEM_SIDES.includes(s) ? s : null;
}

/**
 * May this person take items off this side's list? Admins hold every
 * permission; anyone else needs one of the side's keys.
 *
 * @param {string} side
 * @param {{ isAdmin?: boolean, permissions?: object }} who
 * @returns {boolean}
 */
export function mayRemove(side, { isAdmin = false, permissions = null } = {}) {
  const s = normaliseSide(side);
  if (!s) return false;
  if (isAdmin === true) return true;
  return REMOVAL_PERMISSIONS[s].some(k => !!(permissions && permissions[k] === true));
}

/** The sentence a refusal for want of permission says. */
export function permissionReason(side) {
  const s = normaliseSide(side) || 'customer';
  const labels = REMOVAL_PERMISSIONS[s].map(k => `"${PERMISSION_LABELS[k]}"`);
  const words = `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
  return `Taking an item off a ${s}’s ${REMOVAL_LIST_NAMES[s]} list needs ${words}`;
}

/**
 * Why the X is greyed for this person, or null when it may be used.
 *
 * @param {object} args
 * @param {string} args.side
 * @param {boolean} args.ready       the removals table exists
 * @param {boolean} [args.readOnly]  a view-only (REP) account
 * @param {boolean} [args.isAdmin]
 * @param {object} [args.permissions]
 * @returns {string|null}
 */
export function removeBlock({ side, ready, readOnly = false, isAdmin = false, permissions = null } = {}) {
  if (!ready) return NOT_READY_REASON;
  if (readOnly) return READ_ONLY_REASON;
  if (!mayRemove(side, { isAdmin, permissions })) return permissionReason(side);
  return null;
}

const toTime = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * Is an item kept off the list by a removal? While its latest use is no later
 * than the removal — a use with no recorded moment never counts as later. The
 * SQL in utils/previousItemsSql is this rule.
 *
 * @param {object} args
 * @param {Date|string|null} args.removedAt  when it was removed (null: never)
 * @param {Date|string|null} args.lastUsedAt  the latest moment it was ordered / bought
 * @returns {boolean}
 */
export function hiddenByRemoval({ removedAt, lastUsedAt } = {}) {
  const removed = toTime(removedAt);
  if (removed === null) return false;
  const used = toTime(lastUsedAt);
  return used === null || used <= removed;
}

/** "Mole Valley’s" / "Cowling Bros’" — the list's owner, possessive. */
export function possessive(name) {
  const n = String(name ?? '').trim();
  if (!n) return 'this account’s';
  return /s$/i.test(n) ? `${n}’` : `${n}’s`;
}

const itemWords = ({ code, name } = {}) => [code, name].map(v => String(v ?? '').trim()).filter(Boolean).join(' ') || 'the item';

/**
 * The toast after a removal: "Removed P12193 Supreme Rearer 16 from
 * D J Smith’s recently ordered".
 */
export function removedMessage({ side, code, name, accountName } = {}) {
  const s = normaliseSide(side) || 'customer';
  return `Removed ${itemWords({ code, name })} from ${possessive(accountName)} ${REMOVAL_LIST_NAMES[s]}`;
}

/** After Undo: "P12193 Supreme Rearer 16 is back on D J Smith’s recently ordered". */
export function restoredMessage({ side, code, name, accountName } = {}) {
  const s = normaliseSide(side) || 'customer';
  return `${itemWords({ code, name })} is back on ${possessive(accountName)} ${REMOVAL_LIST_NAMES[s]}`;
}

/** The list's heading, as the history entry names it. */
export function listTitle(side) {
  const s = normaliseSide(side) || 'customer';
  const n = REMOVAL_LIST_NAMES[s];
  return n.charAt(0).toUpperCase() + n.slice(1);
}
